import { UIIcon } from '../../config/icons';
import { UIInputAdapter } from '../ui/component/adapter';
import { UIGroupDropdown } from '../ui/component/groupdropdown';
import { UIList } from '../ui/component/list';
import { $el } from '../ui/elements';
import { UIRelPos } from '../ui/types';
import { UIViewSpec } from '../ui/view';
import { xavis_experiments } from '../y/debug';
import { $on } from '../y/events';
import { yCopy } from '../y/object';
import { EDBlock } from './block';
import { EDButtonSpec, EDEditor } from './editor';

export class EDBlockList<Block extends { type: string }, Extra extends {} = {}> extends UIList {
    blocks: UIInputAdapter<Block, EDBlock<Extra>>;
    editor: EDEditor<any, Block, Extra>;

    constructor(editor: EDEditor<any, Block, Extra>, make_block: (b: Block) => EDBlock<any>) {
        const blocks = new UIInputAdapter<Block, EDBlock<Extra>>(b => {
            const block = make_block(b);
            return { view: block, input: block.form };
        });

        super(blocks);

        this.blocks = blocks;
        this.editor = editor;

        $on(blocks, 'change', () => {
            editor.tree.update();
        });
    }

    getButtons(block?: EDBlock<Extra>): EDButtonSpec[] {
        const rect = this.ui_elem.getBoundingClientRect();
        const blocks = this.blocks.views;

        if(blocks.length) {
            const first_block = block ?? blocks[0];
            const last_block = block ?? blocks[blocks.length - 1];

            const first_rect = first_block.ui_elem.getBoundingClientRect();
            const last_rect = last_block.ui_elem.getBoundingClientRect();

            return [{
                icon: UIIcon.PLUS,
                action: (button: HTMLElement) => {
                    this.insertBlock(first_block, button);
                },
                position: [rect.left + rect.width / 2, first_rect.top - 20],
                tooltip: block ? 'Add block before' : 'Add block at start'
            }, {
                icon: UIIcon.PLUS,
                action: (button: HTMLElement) => {
                    this.insertBlock(last_block, button, true);
                },
                position: [rect.left + rect.width / 2, last_rect.bottom + 20],
                tooltip: block ? 'Add block after' : 'Add block at end'
            }];
        } else {
            return [{
                icon: UIIcon.PLUS,
                action: (button: HTMLElement) => {
                    this.insertBlock(undefined, button);
                },
                position: [rect.left + rect.width / 2, rect.top + rect.height / 2],
                tooltip: 'Add block'
            }];
        }
    }

    insertBlock(rel: EDBlock<Extra> | undefined, anchor: UIViewSpec, after: boolean = false) {
        const blocks = this.blocks;
        const entry = rel ? blocks.findView(rel) : undefined;

        const popup = new UIGroupDropdown(this.editor.new_blocks, b => {
            if(b.experimental && !xavis_experiments) return;
            return { view: $el.DIV('ui-group-dropdown-item', [$el.ICON(b.icon), $el.DIV('r-text', b.name)]) }
        });
        popup.on_choose = v => {
            const new_block = yCopy(v.block);
            const new_entry = after ? blocks.insertAfter(new_block, entry) : blocks.insertBefore(new_block, entry);
            this.editor.selectObject(new_entry?.view);
        };
        popup.open(anchor, { position: UIRelPos.Below | UIRelPos.Center, adjust_size: true, capture_mouse: true, can_flip: true });
    }

    removeBlock(block: EDBlock<Extra>) {
        const blocks = this.blocks;
        const entry = blocks.findView(block);
        if(!entry) return;
        blocks.remove(entry);
    }
}
